// src/screens/ViewEvent.jsx
// Fiche événement côté créateur — design compact :
// image à gauche (taille maîtrisée) + infos à droite, description en dessous.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getEventById } from "../services/CreateurService";
import {
    Activity,
    Eye,
    Loader2,
    Heart,
    Utensils,
    ParkingSquare,
    Volume2,
    Accessibility,
    Clock,
    MapPin,
    Euro,
    Calendar,
    Edit,
    ArrowLeft,
    ExternalLink,
    Video,
    Wifi,
} from "lucide-react";

const VIOLET = "#49179A";

// Parse le champ prixEven qui peut être :
//  - "0" ou vide          → gratuit
//  - un nombre simple     → tarif unique (ancien format)
//  - un JSON [{"titre":"Adulte","prix":"20.45"}, ...] → tarifs multiples
function parseTarifs(prixEven) {
    if (!prixEven) return [];
    const raw = String(prixEven).trim();
    if (raw === "" || raw === "0") return [];

    if (raw.startsWith("[")) {
        try {
            const list = JSON.parse(raw);
            return list
                .filter((t) => t && t.titre)
                .map((t) => ({ titre: t.titre, prix: parseFloat(t.prix) || 0 }));
        } catch (_) {
            return [];
        }
    }

    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) return [{ titre: "Tarif", prix: val }];
    return [];
}

function formatDate(iso) {
    const date = new Date(iso);
    if (isNaN(date)) return null;
    return {
        jour: date.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }),
        heure: date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
        jourNum: date.getDate().toString().padStart(2, "0"),
        moisCourt: date.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "").toUpperCase(),
    };
}

const Chargement = () => (
    <div className="w-full h-[70vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin w-10 h-10 text-color1" />
        <p className="text-gray-500 text-sm">Chargement...</p>
    </div>
);

// Ligne d'info : icône + label + valeur
const LigneInfo = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${VIOLET}12`, color: VIOLET }}>
            <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
            <div className="text-sm text-gray-800">{children}</div>
        </div>
    </div>
);

// Pastille d'accessibilité
const Option = ({ icon: Icon, label }) => (
    <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-100 rounded-full px-2.5 py-1">
        <Icon size={13} /> {label}
    </span>
);

const ViewEvent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const [event, setEvent] = useState(null);
    const [images, setImages] = useState([]);
    const [imageActive, setImageActive] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvent = async () => {
            const eventId = parseInt(id, 10);
            if (isNaN(eventId) || eventId <= 0) {
                toast.error("ID invalide");
                navigate("/EspaceClient/mesevents");
                return;
            }

            try {
                const data = await getEventById(eventId, token);
                const imgs = data.imgEven && data.imgEven !== "empty" ? data.imgEven.split("#").filter(Boolean) : [];
                setEvent(data);
                setImages(imgs);
            } catch (err) {
                toast.error("Erreur lors de la récupération de l'événement");
                navigate("/EspaceClient/mesevents");
            } finally {
                setLoading(false);
            }
        };

        fetchEvent();
    }, [id, navigate, token]);

    if (loading) return <Chargement />;
    if (!event) return null;

    const date = formatDate(event.dateEven);
    const tarifs = parseTarifs(event.prixEven);
    const options = [
        event.isRestaurant === 1 && { icon: Utensils, label: "Restaurant" },
        event.isParking === 1 && { icon: ParkingSquare, label: "Parking" },
        event.isAuditive === 1 && { icon: Volume2, label: "Accès auditif" },
        event.isHandicap === 1 && { icon: Accessibility, label: "Accès PMR" },
    ].filter(Boolean);

    return (
        <div className="w-full flex justify-center pb-10 pt-2 px-3 font-Poppins">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="w-full max-w-5xl flex flex-col gap-4 min-w-0">

                {/* ── Barre d'actions ── */}
                <div className="flex items-center justify-between gap-3">
                    <button
                        onClick={() => navigate("/EspaceClient/mesevents")}
                        className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition"
                    >
                        <ArrowLeft size={16} /> Mes événements
                    </button>
                    <button
                        onClick={() => navigate(`/EspaceClient/mesevents/update/${event.id}`)}
                        className="flex items-center gap-1.5 text-sm font-semibold text-white px-4 py-2 rounded-xl hover:brightness-110 transition"
                        style={{ backgroundColor: VIOLET }}
                    >
                        <Edit size={15} /> Modifier
                    </button>
                </div>

                {/* ── Bloc principal : image + infos ── */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-5">

                    {/* Image — taille maîtrisée */}
                    <div className="flex flex-col gap-2">
                        <div className="relative w-full aspect-[4/5] max-h-[380px] rounded-xl overflow-hidden bg-gray-100">
                            {images.length > 0 ? (
                                <img
                                    src={images[imageActive]}
                                    alt={event.nomEven || ""}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
                                    Aucune image
                                </div>
                            )}

                            {date && (
                                <div className="absolute top-2 left-2 bg-white/95 rounded-lg px-2 py-1 text-center shadow-sm">
                                    <div className="text-base font-extrabold leading-none" style={{ color: VIOLET }}>{date.jourNum}</div>
                                    <div className="text-[10px] font-semibold text-gray-600">{date.moisCourt}</div>
                                </div>
                            )}
                        </div>

                        {/* Miniatures si plusieurs images */}
                        {images.length > 1 && (
                            <div className="flex gap-2">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setImageActive(i)}
                                        className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition ${i === imageActive ? "" : "border-transparent opacity-60 hover:opacity-100"}`}
                                        style={i === imageActive ? { borderColor: VIOLET } : {}}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Infos */}
                    <div className="flex flex-col gap-4 min-w-0">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">{event.nomEven}</h1>
                                <span
                                    className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${event.typeEvent === "En Ligne"
                                            ? "bg-blue-50 text-blue-600 border border-blue-100"
                                            : "bg-purple-50 border"
                                        }`}
                                    style={event.typeEvent !== "En Ligne" ? { color: VIOLET, borderColor: `${VIOLET}25` } : {}}
                                >
                                    {event.typeEvent === "En Ligne" ? (
                                        <><Wifi size={11} /> En ligne</>
                                    ) : (
                                        <><MapPin size={11} /> Présentiel</>
                                    )}
                                </span>
                            </div>
                            {event.sousNomEven && (
                                <p className="italic text-sm mt-1" style={{ color: VIOLET }}>{event.sousNomEven}</p>
                            )}
                            {event.ref && <p className="text-gray-400 text-[11px] mt-1">Réf : {event.ref}</p>}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-xl bg-pink-50 px-3 py-2">
                                <div className="flex items-center gap-1 text-[11px] text-pink-600"><Heart size={12} /> Likes</div>
                                <div className="text-lg font-extrabold text-gray-900">{event.nbr_jaime || 0}</div>
                            </div>
                            <div className="rounded-xl bg-blue-50 px-3 py-2">
                                <div className="flex items-center gap-1 text-[11px] text-blue-600"><Eye size={12} /> Vues</div>
                                <div className="text-lg font-extrabold text-gray-900">{event.nbr_vues || 0}</div>
                            </div>
                            <div className="rounded-xl bg-purple-50 px-3 py-2">
                                <div className="flex items-center gap-1 text-[11px] text-purple-600"><Activity size={12} /> Clics</div>
                                <div className="text-lg font-extrabold text-gray-900">{event.nbr_clicks || 0}</div>
                            </div>
                        </div>

                        {/* Infos pratiques */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {date && (
                                <LigneInfo icon={Calendar} label="Date">
                                    <span className="capitalize">{date.jour}</span> à {date.heure}
                                </LigneInfo>
                            )}

                            {event.duree && (
                                <LigneInfo icon={Clock} label="Durée">{event.duree}</LigneInfo>
                            )}

                            {event.typeEvent === "En Ligne" ? (
                                <LigneInfo icon={Video} label="Format">
                                    <span className="flex items-center gap-1.5">
                                        <Wifi size={13} className="text-blue-500" /> Événement en ligne
                                    </span>
                                </LigneInfo>
                            ) : (
                                <LigneInfo icon={MapPin} label="Adresse">
                                    {event.rueEven && `${event.rueEven}, `}
                                    {event.codePostalEven} {event.villeEven}
                                </LigneInfo>
                            )}

                            <LigneInfo icon={Euro} label="Tarif">
                                {event.isGratuit ? (
                                    <span className="font-semibold text-green-600">Gratuit</span>
                                ) : tarifs.length === 0 ? (
                                    <span className="text-gray-400">Non renseigné</span>
                                ) : (
                                    <div className="flex flex-col gap-1 mt-0.5">
                                        {tarifs.map((t, i) => (
                                            <div key={i} className="flex justify-between gap-3 text-xs bg-gray-50 rounded-md px-2 py-1">
                                                <span className="text-gray-600 truncate">{t.titre}</span>
                                                <span className="font-semibold shrink-0" style={{ color: VIOLET }}>{t.prix.toFixed(2)} €</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </LigneInfo>
                        </div>

                        {/* Accessibilité — n'a de sens qu'en présentiel */}
                        {event.typeEvent !== "En Ligne" && options.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {options.map((o) => <Option key={o.label} icon={o.icon} label={o.label} />)}
                            </div>
                        )}

                        {/* Boutons d'action : rejoindre la réunion + réserver */}
                        <div className="mt-auto flex flex-wrap gap-2">
                            {event.typeEvent === "En Ligne" && event.lien_reunion && (
                                <a
                                    href={event.lien_reunion}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full sm:w-fit text-sm font-semibold text-white px-5 py-2.5 rounded-xl shadow-sm hover:brightness-110 transition bg-blue-600"
                                >
                                    <Video size={15} /> Rejoindre la réunion
                                </a>
                            )}
                            {event.site && (
                                <a
                                    href={event.site}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full sm:w-fit text-sm font-semibold text-white px-5 py-2.5 rounded-xl shadow-sm hover:brightness-110 transition"
                                    style={{ backgroundColor: VIOLET }}
                                >
                                    <ExternalLink size={15} /> Réserver ici
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Description ── */}
                {event.description && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 min-w-0 overflow-hidden">
                        <h2 className="text-sm font-bold text-gray-800 mb-3">Description</h2>
                        <div
                            className="prose prose-sm prose-gray max-w-none text-justify break-words [overflow-wrap:anywhere] [&_*]:break-words [&_*]:[overflow-wrap:anywhere] [&_a]:break-all"
                            dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewEvent;